import { DatePicker } from "@mui/x-date-pickers";
import { useState } from "react";
import dayjs from "dayjs";

import { Button, Checkbox, FormControl, FormHelperText, InputLabel, MenuItem, Select } from "@mui/material";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import AddIcon from '@mui/icons-material/Add';

import { Subtitle, Button as CustomButton } from "../styled";
import { TextField, Container } from "./styled";
import { useMissingData } from "./context";
import { IDependentForm } from "../types";
import { useAlert } from "contexts/alert";
import { cpfMask } from "utils/cpf";
import {
  DEPENDENT_TYPE_OPTIONS,
  GENDER_OPTIONS,
  MARITAL_STATUS_OPTIONS,
} from "v2/constants/esocial-options";

export function StepDependents() {
  const { data, config, handlePrevStep, handleNextStep, handleUpdateData } = useMissingData();
  const { openAlert, closeAlert } = useAlert();

  const [editingDataErrors, setEditingDataErrors] = useState<Record<string, string>[]>(data.dependents.map(_ => ({})));
  const [editingData, setEditingData] = useState(data.dependents);
  const [isEditing, setIsEditing] = useState(false);

  // Seguindo o padrão do ViewEditingData.tsx
  const alwaysMandatoryFields = ["name", "taxPayerId"];
  const alwaysVisibleFields = ["name", "taxPayerId", "dependentType", "student", "gender"]; // Campos específicos de dependentes sempre visíveis
  const visibleFields = [...config.filter(field => !!field.visible).map(field => field.key), ...alwaysVisibleFields];
  const requiredFields = [...config.filter(field => !!field.required).map(field => field.key), ...alwaysMandatoryFields];

  // Função para mapear as chaves da configuração para as chaves do formulário de dependentes
  function getFieldKey(configKey: string): string | null {
    const keyMapping: Record<string, string> = {
      "name": "name",
      "taxPayerId": "tax_payer_id", 
      "motherName": "mother_name",
      "birthDate": "birth_date",
      "maritalStatus": "marital_status_id",
      // Campos específicos de dependentes
      "dependentType": "dependent_type_id",
      "student": "student",
      "gender": "gender_id",
      "productType": "product_type"
    };
    
    return keyMapping[configKey] || null;
  }

  function handleNext() {
    /** Validation rules. */
    let rulesArray = new Array();
    const hasSpecialCharacters = /[^a-zA-Z0-9À-ÿ ]/;
    const hasLetterBetweenSpaces = /\s[a-zA-Z]\s/;
    
    editingData.forEach(dependentData => {
      let rules = {};

      /** Benefit rules - sempre obrigatórios */
      if (!dependentData.product_type.length) rules = { ...rules, ["product_type"]: "Campo obrigatório." };

      config.forEach(field => {
        if (!!field.visible) {
          /** Regra de campo obrigatório é aplicada conforme o que foi especificado na configuração. */
          if (!!field.required) {
            const fieldKey = getFieldKey(field.key);
            if (fieldKey && !dependentData?.[fieldKey as keyof typeof dependentData]) {
              rules = { ...rules, [fieldKey]: "Campo obrigatório." };
            }
          }

          /** Campos que sempre serão mandatórios independente da regra estabelecida. */
          if (alwaysMandatoryFields.includes(field.key)) {
            const fieldKey = getFieldKey(field.key);
            if (fieldKey && !dependentData?.[fieldKey as keyof typeof dependentData]) {
              rules = { ...rules, [fieldKey]: "Campo obrigatório." };
            }
          }

          /** Independente de ser obrigatório ou não, validações que acontecem pra campos específicos caso esteja editando eles. */
          if (field.key === "taxPayerId" && !!dependentData?.tax_payer_id && (dependentData.tax_payer_id.length !== 11 || isNaN(Number(dependentData.tax_payer_id)))) rules = { ...rules, ["tax_payer_id"]: "CPF inválido." };
          if (field.key === "name" && !!dependentData?.name && hasSpecialCharacters.test(dependentData.name)) rules = { ...rules, ["name"]: "Não permitido caracteres especiais." };
          if (field.key === "name" && !!dependentData?.name && hasLetterBetweenSpaces.test(dependentData.name)) rules = { ...rules, ["name"]: "Não permitido abreviações." };
          if (field.key === "motherName" && !!dependentData?.mother_name && hasSpecialCharacters.test(dependentData.mother_name)) rules = { ...rules, ["mother_name"]: "Não permitido caracteres especiais." };
          if (field.key === "motherName" && !!dependentData?.mother_name && hasLetterBetweenSpaces.test(dependentData.mother_name)) rules = { ...rules, ["mother_name"]: "Não permitido abreviações." };
        }
      });

      rulesArray.push(rules);
    });

    /** If there is any error in any field for any dependent in the array, it returns with the error. */
    if (rulesArray.find(rules => Object.entries(rules).length > 0)) {
      setEditingDataErrors(rulesArray);
      return openAlert('Verifique se os campos preenchidos estão corretos.', 'error');
    }
    
    handleNextStep();
  }

  function handleEdit() {
    closeAlert();
    setIsEditing(true);
  }

  function handlePrev() {
    closeAlert();
    handlePrevStep();
  }

  function handleCancel() {
    setEditingData(data.dependents);
    setEditingDataErrors(data.dependents.map(_ => ({})));
    setIsEditing(false);
  }

  function handleSave() {
    handleUpdateData({ ...data, dependents: editingData });
    setIsEditing(false);
  }

  function handleEditDependente(index: number, payload: Partial<IDependentForm>) {
    setEditingData((prev) => {
      const previousCopy = [...prev];
      //@ts-ignore
      previousCopy[index] = { ...payload };
      return [...previousCopy];
    });
  }

  function handleAddDependente() {
    if (editingData.length === 10) return;

    const newDependent: IDependentForm = {
      birth_date: null,
      product_type: [],
      dependent_type_id: "",
      marital_status_id: "",
      mother_name: "",
      name: "",
      plan_option: "",
      student: "",
      tax_payer_id: "",
      gender_id: "",
      key: String(Math.random()),
    };

    setEditingData(prev => [...prev, newDependent]);
    setEditingDataErrors(prev => [...prev, {}]);
  }

  function handleRemoveDependent(index: number) {
    setEditingData(prev => prev.filter((_, dIndex) => dIndex !== index ));
    setEditingDataErrors(prev => prev.filter((_, dIndex) => dIndex !== index));
  }

  return (
    <>
      <div style={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginTop: "20px" }}>
        <Subtitle>Dados do(s) dependente(s)</Subtitle>
        <div style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
          <Button variant="outlined" size="small" disabled={isEditing} onClick={handleEdit}>Editar dados</Button>
        </div>
      </div>
      {editingData.map((dependent, index) => (
        <Container>
          <div key={dependent.key} style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            <span style={{ fontWeight: "500" }}>Dependente {index + 1}</span>
            <div style={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ width: "100%", display: "flex", flexDirection: "row", gap: "24px", flexWrap: "wrap" }}>
                {visibleFields.includes("name") && (
                  <TextField
                    label={`Nome completo sem abreviações${requiredFields.includes("name") ? '*' : ''}`}
                    value={editingData[index].name}
                    onChange={(e) => handleEditDependente(index, { ...editingData[index], name: e.target.value })}
                    disabled={!isEditing}
                    error={!!editingDataErrors[index]["name"]}
                    helperText={editingDataErrors[index]["name"]}
                  />
                )}
                {visibleFields.includes("dependentType") && (
                  <FormControl disabled={!isEditing} fullWidth style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }} error={!!editingDataErrors[index].dependent_type_id}>
                    <InputLabel>{`Tipo do dependente${requiredFields.includes("dependentType") ? '*' : ''}`}</InputLabel>
                    <Select
                      label={`Tipo do dependente${requiredFields.includes("dependentType") ? '*' : ''}`}
                      value={editingData[index].dependent_type_id}
                      onChange={(e) => handleEditDependente(index, { ...editingData[index], dependent_type_id: e.target.value })}
                    >
                      {DEPENDENT_TYPE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </Select>
                    {!!editingDataErrors[index]["dependent_type_id"] && <FormHelperText error>{editingDataErrors[index]["dependent_type_id"]}</FormHelperText>}
                  </FormControl>
                )}
                {visibleFields.includes("maritalStatus") && (
                  <FormControl disabled={!isEditing} fullWidth style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }} error={!!editingDataErrors[index].marital_status_id}>
                    <InputLabel>{`Estado civil${requiredFields.includes("maritalStatus") ? '*' : ''}`}</InputLabel>
                    <Select
                      label={`Estado civil${requiredFields.includes("maritalStatus") ? '*' : ''}`}
                      value={editingData[index].marital_status_id}
                      onChange={(e) => handleEditDependente(index, { ...editingData[index], marital_status_id: e.target.value })}
                    >
                      {MARITAL_STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </Select>
                    {!!editingDataErrors[index]["marital_status_id"] && <FormHelperText error>{editingDataErrors[index]["marital_status_id"]}</FormHelperText>}
                  </FormControl>
                )}
                {visibleFields.includes("motherName") && (
                  <TextField
                    label={`Nome completo da mãe sem abreviações${requiredFields.includes("motherName") ? '*' : ''}`}
                    value={editingData[index].mother_name}
                    onChange={(e) => handleEditDependente(index, { ...editingData[index], mother_name: e.target.value })}
                    disabled={!isEditing}
                    error={!!editingDataErrors[index]["mother_name"]}
                    helperText={editingDataErrors[index]["mother_name"]}
                  />
                )}
                {visibleFields.includes("taxPayerId") && (
                  <TextField
                    label={`CPF${requiredFields.includes("taxPayerId") ? '*' : ''}`}
                    value={cpfMask(editingData[index].tax_payer_id)}
                    onChange={(e) => handleEditDependente(index, { ...editingData[index], tax_payer_id: e.target.value.replace(/\D/g, '').substring(0, 11) })}
                    disabled={!isEditing}
                    error={!!editingDataErrors[index]["tax_payer_id"]}
                    helperText={editingDataErrors[index]["tax_payer_id"]}
                  />
                )}
                {visibleFields.includes("birthDate") && (
                  <DatePicker
                    label={`Data de nascimento${requiredFields.includes("birthDate") ? '*' : ''}`}
                    format="DD / MM / YYYY"
                    value={dayjs(editingData[index].birth_date)}
                    onChange={(e) => handleEditDependente(index, { ...editingData[index], birth_date: e?.format("YYYY-MM-DD") })}
                    slotProps={{ textField: { error: !editingData[index].birth_date, style: { minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" } }}}
                    disabled={!isEditing}
                  />
                )}
                {visibleFields.includes("gender") && (
                  <FormControl disabled={!isEditing} fullWidth style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }} error={!!editingDataErrors[index]["gender_id"]}>
                    <InputLabel id={`${index}_genero`}>{`Gênero${requiredFields.includes("gender") ? '*' : ''}`}</InputLabel>
                    <Select
                      labelId={`${index}_genero`}
                      label={`Gênero${requiredFields.includes("gender") ? '*' : ''}`}
                      value={editingData[index].gender_id || ""}
                      onChange={(e) => handleEditDependente(index, { ...editingData[index], gender_id: e.target.value })}
                    >
                      {GENDER_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </Select>
                    {!!editingDataErrors[index]["gender_id"] && <FormHelperText error>{editingDataErrors[index]["gender_id"]}</FormHelperText>}
                  </FormControl>
                )}
                {visibleFields.includes("student") && (
                  <FormControl disabled={!isEditing} fullWidth style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }} error={!!editingDataErrors[index]["student"]}>
                    <InputLabel id={`${index}_estudante`}>{`Estudante${requiredFields.includes("student") ? '*' : ''}`}</InputLabel>
                    <Select
                      labelId={`${index}_estudante`}
                      label={`Estudante${requiredFields.includes("student") ? '*' : ''}`}
                      value={editingData[index].student}
                      onChange={(e) => handleEditDependente(index, { ...editingData[index], student: e.target.value })}
                    >
                      <MenuItem value={'SIM'}>Sim</MenuItem>
                      <MenuItem value={'NAO'}>Não</MenuItem>
                    </Select>
                    {!!editingDataErrors[index]["student"] && <FormHelperText error>{editingDataErrors[index]["student"]}</FormHelperText>}
                  </FormControl>
                )}
              </div>
            </div>
            <span style={{ fontWeight: "500" }}>Benefícios do dependente</span>
            <div style={{ display: "flex", flexDirection: "row", gap: "24px", marginTop: "-20px" }}>
              <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                <Checkbox
                  size="small"
                  checked={editingData[index].product_type.includes("NONE")}
                  disabled={!isEditing}
                  sx={{ "&.Mui-disabled svg": { fill: "rgb(112, 121, 116)" }}}
                  onChange={() => !editingData[index].product_type.includes("NONE") ? handleEditDependente(index, { ...editingData[index], product_type: ["NONE"] }) : handleEditDependente(index, { ...editingData[index], product_type: [] })}
                />
                <span style={{ color: !isEditing ? "rgb(112, 121, 116)" : "initial" }}>Não optante pelo benefício</span>
              </div>
              {data.dependent_product_type.includes("HEALTH") && (
                <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                  <Checkbox
                    size="small"
                    checked={editingData[index].product_type.includes("HEALTH")}
                    disabled={!isEditing}
                    sx={{ "&.Mui-disabled svg": { fill: "rgb(112, 121, 116)" }}}
                    onChange={() => !editingData[index].product_type.includes("HEALTH") ? handleEditDependente(index, { ...editingData[index], product_type: [...editingData[index].product_type.filter(p => p !== "NONE"), "HEALTH"] }) : handleEditDependente(index, { ...editingData[index], product_type: [...editingData[index].product_type.filter(p => p !== "HEALTH")] })} 
                  />
                  <span style={{ color: !isEditing ? "rgb(112, 121, 116)" : "initial" }}>Saúde</span>
                </div>
              )}
              {data.dependent_product_type.includes("LIFE") && (
                <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                  <Checkbox
                    size="small"
                    checked={editingData[index].product_type.includes("LIFE")}
                    disabled={!isEditing}
                    sx={{ "&.Mui-disabled svg": { fill: "rgb(112, 121, 116)" }}}
                    onChange={() => !editingData[index].product_type.includes("LIFE") ? handleEditDependente(index, { ...editingData[index], product_type: [...editingData[index].product_type.filter(p => p !== "NONE"), "LIFE"] }) : handleEditDependente(index, { ...editingData[index], product_type: [...editingData[index].product_type.filter(p => p !== "LIFE")] })} 
                  />
                  <span style={{ color: !isEditing ? "rgb(112, 121, 116)" : "initial" }}>Vida</span>
                </div>
              )}
              {data.dependent_product_type.includes("ODONTO") && (
                <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                  <Checkbox
                    size="small"
                    checked={editingData[index].product_type.includes("ODONTO")}
                    disabled={!isEditing}
                    sx={{ "&.Mui-disabled svg": { fill: "rgb(112, 121, 116)" }}}
                    onChange={() => !editingData[index].product_type.includes("ODONTO") ? handleEditDependente(index, { ...editingData[index], product_type: [...editingData[index].product_type.filter(p => p !== "NONE"), "ODONTO"] }) : handleEditDependente(index, { ...editingData[index], product_type: [...editingData[index].product_type.filter(p => p !== "ODONTO")] })} 
                  />
                  <span style={{ color: !isEditing ? "rgb(112, 121, 116)" : "initial" }}>Odonto</span>
                </div>
              )}
            </div>
            {editingDataErrors[index]["product_type"] && <FormHelperText error style={{ marginTop: "-20px", marginLeft: "12px" }}>{editingDataErrors[index]["product_type"]}</FormHelperText>}
          </div>
          {isEditing && <Button variant="outlined" color="error" onClick={() => handleRemoveDependent(index)}>Remover dependente</Button>}
        </Container>
      ))}
      {isEditing && editingData.length < 10 && (
        <Button
          variant="text"
          onClick={() => handleAddDependente()}
          startIcon={<AddIcon color="primary" />}
          style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
        >
          Adicionar dependente
        </Button>
      )}
      <div style={{ width: "100%", display: "flex", justifyContent: "end" }}>
        <div style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
          {!isEditing && (
            <>
              <CustomButton 
                variant="outlined"
                disabled={isEditing}
                onClick={handlePrev}
                startIcon={<ChevronLeftIcon color="primary" style={{ height: "22px", marginLeft: "-8px"}} />}
              >
                Voltar
              </CustomButton>
              <CustomButton 
                variant="contained"
                disabled={isEditing}
                onClick={handleNext}
              >
                Próximo
              </CustomButton>
            </>
          )}
          {isEditing && (
            <>
              <CustomButton variant="text" color="error" onClick={handleCancel}>Cancelar</CustomButton>
              <CustomButton variant="contained" onClick={handleSave} style={{ width: "fit-content" }}>Salvar edições</CustomButton>
            </>
          )}
        </div>
      </div>
    </>
  )
}
